import React, { useEffect, useRef } from "react";
import { removeUser } from "./Redux/Actions/UserActions";
import { getAuthTokens } from "./utils/cookieUtils";
import { useDispatch } from "react-redux";
import { useWebSocket } from "./hooks/useWebSocket";

import TopNavigation from "./component/NavComponents/TopNavigation";
import SignIn from "./Pages/AuthPages/SignIn";
import SignUp from "./Pages/AuthPages/SignUp";
import AuthorizedRoute from "./component/AuthorizedRoute";
import Portfolio from "./Pages/Portfolio/Portfolio";
import BrokerPage from "./Pages/Broker";
import Funds from "./Pages/Funds";
import Strategies from "./Pages/Strategies";
import Custodian from "./Pages/Custodian";
import TradeBlotter from "./Pages/TradeBlotter";
import PerformanceDashboard from "./Pages/PerformanceDashboard/PerformanceDashboard";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import FundAllocator from "./Pages/FundAllocator/FundAllocator";
import Signals from "./Pages/Signals";
import Rebalancer from "./Pages/Rebalancer/Rebalancer";
import Accounting from "./Pages/Accounting";
import ForgotUserPassword from "./Pages/AuthPages/ForgotUserPassword";
import ResetUserPassword from "./Pages/AuthPages/ResetUserPassword";
import ResetPasswordOTP from "./Pages/AuthPages/ResetPasswordOTP";
import SecurityMaster from "./Pages/SecurityMaster";
import ReportsPage from "./Pages/Reports";
import HistoricalReports from "./Pages/Reports/HistoricalReports";
import Positions from "./Pages/Positions";
import FundsBlacklist from "./Pages/FundsBlacklist";
import BoxedPositionsTable from "./Pages/BoxedPositions";
import Store from "./Pages/Store";
import StrategyDetails from "./Pages/Store/Strategy/index";
import StrategyComparison from "./Pages/Store/Strategy/Comparison/index";
import Strategist from "./Pages/Strategist/index"
import StrategyPerformance from "./Pages/StrategyPerformance";
import ManagerOverview from "./Pages/ManagerOverview/ManagerOverviewDashboard";
import AddStrategyPage from "./Pages/Strategies/Add/index";

// used for public store routes accessible without login.
const PublicStoreLayout = ({ children }) => (
  <div className="app">
    <main className="content">{children}</main>
  </div>
);

// Define a configuration array for routesf
const routesConfig = [
  { path: '/portfolio', component: Portfolio },
  { path: '/portfolio/:fundName', component: Portfolio },
  { path: '/portfolio/:fundName/:strategyName', component: Portfolio },
  { path: '/funds', component: Funds },
  { path: '/strategies', component: Strategies },
  { path: '/strategies/add', component: AddStrategyPage },
  { path: '/broker', component: BrokerPage },
  { path: '/custodian', component: Custodian },
  { path: '/security', component: SecurityMaster },
  { path: '/funds-blacklist', component: FundsBlacklist },
  { path: '/reports', component: ReportsPage },
  { path: '/historical-reports', component: HistoricalReports },
  { path: '/positions', component: Positions },
  { path: '/boxed-positions', component: BoxedPositionsTable },
  { path: '/tradesV2', component: TradeBlotter },
  { path: '/fundAllocator', component: FundAllocator },
  { path: '/signals', component: Signals },
  { path: '/rebalancer', component: Rebalancer },
  { path: '/accounting', component: Accounting },
  { path: '/vantage', component: PerformanceDashboard },
  { path: '/manager-overview', component: ManagerOverview },
  { path: '/store', component: Store },
  { path: '/store/strategy/:ownerSlug/:slug', component: StrategyDetails },
  { path: '/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs', component: StrategyComparison },
  { path: '/strategist/:strategistId', component: Strategist },
  { path: '/strategy-performance', component: StrategyPerformance },

];

// Helper function to render routes
const renderRoutes = (routes) => {
  
  return routes.map(({ path, component: Component }) => (
    <Route
      key={path}
      path={path}
      element={
        <AuthorizedRoute>
          <Component />
        </AuthorizedRoute>
      }
    />
  ));
};

function MainRoutes(props) {
  
  const hasAuth = props.token; // In-memory auth token (user session state)
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { disconnect } = useWebSocket();

  const managers = props.managers;
  const defaultAppPath =
    Array.isArray(managers) && managers.length > 1 ? "/manager-overview" : "/vantage";

  const multiManagerLandingRedirectDoneRef = useRef(false);
  useEffect(() => {
    if (!hasAuth) {
      multiManagerLandingRedirectDoneRef.current = false;
    }
  }, [hasAuth]);
  useEffect(() => {
    if (!hasAuth || !Array.isArray(managers) || managers.length <= 1) return;
    if (location.pathname !== "/vantage" || multiManagerLandingRedirectDoneRef.current) return;
    multiManagerLandingRedirectDoneRef.current = true;
    navigate("/manager-overview", { replace: true });
  }, [hasAuth, managers, location.pathname, navigate]);
  
  useEffect(() => {
    const tokens = getAuthTokens(); // Check for the token in cookies
    // If session exists in memory but not in storage, force logout
    if (!tokens && hasAuth) {
      dispatch(removeUser());
      disconnect();
      navigate("/login", { replace: true });
    }
  }, [location.pathname]);
  
  return (
    <>
      {!hasAuth ? (
        <>
          <Routes>
            <Route path="/" element={<SignIn />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot" element={<ForgotUserPassword />} />
            <Route path="/resetPass" element={<ResetUserPassword />} />
            <Route path="/reset-otp" element={<ResetPasswordOTP />} />
            <Route path="/store/strategy/:ownerSlug/:slug" element={<PublicStoreLayout><StrategyDetails /></PublicStoreLayout>} />
            <Route path="/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs" element={<PublicStoreLayout><StrategyComparison /></PublicStoreLayout>} />
            <Route path="/store" element={<PublicStoreLayout><Store /></PublicStoreLayout>} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </>
      ) : (
        <>
          <div className="app">
            <TopNavigation />
            <main className="content">
              {props.managers && props.managers.length > 0 ? (
                <>
                  <Routes>
                    {renderRoutes(routesConfig)}
                    <Route path="*" element={<Navigate to={defaultAppPath} replace />} />
                  </Routes>
                </>
              ) : (
                <>
                  <Routes>
                    <Route path="/" element={<Navigate to={defaultAppPath} replace />} />
                    <Route path="/login" element={<Navigate to={defaultAppPath} replace />} />
                    {renderRoutes(routesConfig.filter(route => ['/portfolio', '/signals', '/strategies', '/strategies/add', '/vantage', '/manager-overview', '/strategy-performance', '/store', '/store/strategy/:ownerSlug/:slug', '/store/strategy/:ownerSlug/:strategySlug/compare/:compareSlugs'].includes(route.path)))}
                    <Route path="*" element={<Navigate to={defaultAppPath} replace />} />
                  </Routes>
                </>
              )}
            </main>
          </div>
        </>
      )}
    </>
  );
}

export default MainRoutes;
